import type { AgentVersion } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';

import { MarkdownView } from '@/components/markdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgent, useSetActiveVersion } from '@/lib/api/agents';
import { ApiError } from '@/lib/api/client';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function AgentDetailPage({ agentId }: { agentId: string }) {
  const agentQuery = useAgent(agentId);
  const setActive = useSetActiveVersion(agentId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Default the dropdown to whatever is active, and re-sync when the active
  // pointer moves (e.g. after a rollback) rather than stranding a stale pick.
  const activeVersionId = agentQuery.data?.activeVersionId;
  useEffect(() => {
    if (activeVersionId) {
      setSelectedId(activeVersionId);
    }
  }, [activeVersionId]);

  if (agentQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (agentQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load agent: {agentQuery.error.message}
      </p>
    );
  }

  const agent = agentQuery.data;
  const selected: AgentVersion | undefined =
    agent.versions.find((version) => version.id === selectedId) ??
    agent.versions.find((version) => version.id === agent.activeVersionId);

  if (!selected) {
    return <p className="text-sm text-[var(--color-muted-foreground)]">Agent not found.</p>;
  }

  const isActive = selected.id === agent.activeVersionId;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{selected.name}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            <Badge variant="secondary">{selected.slug}</Badge>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline">
            <Link to="/agents/$agentId/edit" params={{ agentId }}>
              <Pencil className="size-4" />
              Edit
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/agents">Back</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Version</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-2">
              <Label htmlFor="version">Viewing version</Label>
              <Select value={selected.id} onValueChange={setSelectedId}>
                <SelectTrigger id="version" className="w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {agent.versions.map((version) => (
                    <SelectItem key={version.id} value={version.id}>
                      v{version.versionNumber}
                      {version.id === agent.activeVersionId ? ' (active)' : ''} — {version.slug}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isActive ? (
              <Badge variant="secondary">Active</Badge>
            ) : (
              <Button
                disabled={setActive.isPending}
                onClick={() => {
                  setError(null);
                  setActive.mutate(selected.id, {
                    onError: (caught) =>
                      setError(
                        caught instanceof ApiError
                          ? caught.message
                          : 'Could not set the active version.',
                      ),
                  });
                }}
              >
                {setActive.isPending ? 'Activating…' : 'Set as active'}
              </Button>
            )}
          </div>
          {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

          <dl className="grid gap-4 sm:grid-cols-2">
            <Field label="Model tier" value={selected.modelTier} />
            <Field label="Request budget per run" value={`${selected.maxRequestsPerRun}`} />
            <Field label="Version number" value={`v${selected.versionNumber}`} />
            <Field label="Updated by" value={selected.updatedByEmail} />
            <Field label="Created" value={formatDate(selected.createdAt)} />
          </dl>

          <Field label="Description" value={selected.description || '—'} />

          <div className="space-y-1">
            <dt className="text-sm font-medium">Enabled tools</dt>
            <dd className="flex flex-wrap gap-2">
              {selected.enabledTools.length ? (
                selected.enabledTools.map((tool) => (
                  <Badge key={tool} variant="outline">
                    {tool}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-[var(--color-muted-foreground)]">None</span>
              )}
            </dd>
          </div>

          <div className="space-y-1">
            <dt className="text-sm font-medium">System prompt</dt>
            <dd>
              <MarkdownView value={selected.systemPrompt} />
            </dd>
          </div>

          <div className="space-y-1">
            <dt className="text-sm font-medium">Voice prompt</dt>
            <dd>
              {selected.voicePrompt.trim() ? (
                <MarkdownView value={selected.voicePrompt} />
              ) : (
                <span className="text-sm text-[var(--color-muted-foreground)]">—</span>
              )}
            </dd>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <dt className="text-sm font-medium">{label}</dt>
      <dd className="text-sm text-[var(--color-muted-foreground)]">{value}</dd>
    </div>
  );
}
